import { config } from '../lib/config';

interface FeatureSectionProps {
  id: string;
  systemId: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
}

function FeatureSection({
  id,
  systemId,
  title,
  subtitle,
  description,
  features,
  imageSrc,
  imageAlt,
  reverse = false,
}: FeatureSectionProps) {
  return (
    <section id={id} className="py-24 px-6 border-b border-border-muted">
      <div className="max-w-7xl mx-auto">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${reverse ? 'lg:flex-row-reverse' : ''}`}>
          {/* Content */}
          <div className={reverse ? 'lg:order-2' : ''}>
            <div className="font-mono text-text-dim text-xs uppercase tracking-widest mb-4">
              {systemId}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {title}
            </h2>
            <p className="text-cyan text-lg mb-6">{subtitle}</p>
            <p className="text-text-secondary text-lg mb-8 leading-relaxed">
              {description}
            </p>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center text-text-secondary">
                  <span className="w-1.5 h-1.5 bg-cyan mr-3 flex-shrink-0"></span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Image */}
          <div className={reverse ? 'lg:order-1' : ''}>
            <div className="border border-border-muted overflow-hidden">
              <img
                src={imageSrc}
                alt={imageAlt}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Home() {
  const features: FeatureSectionProps[] = [
    // MY WORK
    {
      id: 'daily-workspace',
      systemId: 'MY_WORK.TODAY',
      title: 'Daily Workspace',
      subtitle: 'The Execution Engine',
      description: 'Initialize your workspace. Activate tasks from backlog to timeline. Every completion timestamped. Every action logged.',
      features: [
        'Golden Ratio layout (38% objectives, 62% execution)',
        'Task activation flow from drafts to timeline',
        'Timestamped completions for accountability',
        'Daily checkout ritual to terminate session',
      ],
      imageSrc: '/screenshots/daily-workspace.jpg',
      imageAlt: 'Daily Workspace interface showing task execution timeline',
    },
    {
      id: 'daily-checkout',
      systemId: 'MY_WORK.CHECKOUT',
      title: 'Daily Checkout',
      subtitle: 'Termination Sequence',
      description: 'End each day with intention. Log hours worked, summarize accomplishments. The ritual that closes the execution loop.',
      features: [
        'Hours logged tracking for accountability',
        'Daily log for accomplishment summaries',
        'Finalize & Close to seal the day',
        'Abort option for incomplete sessions',
      ],
      imageSrc: '/screenshots/daily-checkout.jpg',
      imageAlt: 'Daily Checkout modal showing termination sequence',
      reverse: true,
    },
    {
      id: 'weekly-planner',
      systemId: 'MY_WORK.WEEKLY',
      title: 'Weekly Planner',
      subtitle: 'The Strategic Grid',
      description: 'Define your sprint objectives. Track execution across the 5-day matrix. Visual commitment to your weekly goals.',
      features: [
        'Sprint initialization with goal setup',
        'Goal-task hierarchy for clear priorities',
        'Day-by-day execution tracking',
        'Checkout summaries for each day',
      ],
      imageSrc: '/screenshots/weekly-planner.jpg',
      imageAlt: 'Weekly Planner showing personal 5-day strategic grid',
    },
    // TEAM
    {
      id: 'team-radar',
      systemId: 'TEAM.DAILY',
      title: 'Team Radar',
      subtitle: "Today's Diagnostic",
      description: 'Real-time alignment view. See what everyone is executing today. Identity cards with live status updates.',
      features: [
        'Identity cards for each team member',
        'Live status updates throughout day',
        'Checkout summaries with completion stats',
        'Full task visibility across team',
      ],
      imageSrc: '/screenshots/team-radar.jpg',
      imageAlt: 'Team Radar showing daily team alignment',
      reverse: true,
    },
    {
      id: 'weekly-overview',
      systemId: 'TEAM.WEEKLY',
      title: 'Weekly Overview',
      subtitle: 'Team Performance Grid',
      description: 'See your entire team trajectory at a glance. Operator objectives mapped across the week. High-density status tracking.',
      features: [
        'Operator/Objective rows with avatar badges',
        'Mon-Sat column grid for full week visibility',
        'Active/Done/Issue status indicators',
        'Cross-day task completion tracking',
      ],
      imageSrc: '/screenshots/weekly-overview.jpg',
      imageAlt: 'Weekly Overview showing team performance matrix',
    },
    // COMPANY
    {
      id: 'organization-radar',
      systemId: 'COMPANY.RADAR',
      title: 'Organization Radar',
      subtitle: 'Fleet Status Overview',
      description: 'Multi-team visibility at the organization level. Unit health monitoring with velocity metrics and availability tracking.',
      features: [
        'Team unit cards with status badges (Optimal, Degraded, Critical)',
        'Availability ratios and sprint milestones',
        'Member avatars with online/offline status',
        'Velocity metrics with percentage indicators',
      ],
      imageSrc: '/screenshots/organization-radar.jpg',
      imageAlt: 'Organization Radar showing fleet status and team health',
      reverse: true,
    },
  ];

  return (
    <div className="min-h-screen bg-canvas">
      {/* Hero Section */}
      <section className="min-h-[90vh] flex flex-col justify-center px-6 pt-24 border-b border-border-muted">
        <div className="max-w-6xl mx-auto">
          {/* System Status Indicator */}
          <div className="flex items-center gap-2 mb-8">
            <span className="w-2 h-2 bg-green animate-pulse"></span>
            <span className="font-mono text-text-dim text-sm uppercase tracking-widest">
              SYSTEM.ONLINE // WORKSPACE.READY
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Initialize Your
            <span className="text-cyan"> Execution</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-text-secondary max-w-3xl mb-12 leading-relaxed">
            High-performance productivity platform for technical teams.
            Brutalist precision. Zero noise. Maximum output.
          </p>

          {/* Hero Screenshot */}
          <div className="border border-border-muted overflow-hidden">
            <img
              src="/screenshots/dayview.jpg"
              alt="DayCheckin workspace showing daily execution timeline"
              className="w-full h-auto"
            />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-12">
            <a
              href={config.signupUrl}
              className="inline-block px-8 py-4 bg-[#FFD93D] text-black font-mono text-sm uppercase tracking-wider hover:opacity-90 transition-opacity cursor-pointer text-center"
            >
              Initialize Workspace
            </a>
            <a
              href="#features"
              className="inline-block px-8 py-4 border border-border-focus text-white font-mono text-sm uppercase tracking-wider hover:border-white transition-colors cursor-pointer text-center"
            >
              View Systems
            </a>
          </div>

          {/* Timestamp */}
          <div className="mt-8 font-mono text-text-dim text-xs uppercase tracking-wider">
            PLATFORM.VERSION // 1.0.0 | STATUS.ACTIVE
          </div>
        </div>
      </section>

      {/* Features Introduction */}
      <section id="features" className="py-24 px-6 border-b border-border-muted bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono text-text-dim text-xs uppercase tracking-widest mb-4">
            SYS.MODULES
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Core Systems
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed">
            Six integrated modules across My Work, Team, and Company views.
            Each system designed for maximum information density and temporal precision.
          </p>
        </div>
      </section>

      {/* Feature Sections */}
      {features.map((feature) => (
        <FeatureSection key={feature.id} {...feature} />
      ))}

      {/* Philosophy Section */}
      <section className="py-24 px-6 border-b border-border-muted bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono text-text-dim text-xs uppercase tracking-widest mb-4">
            DESIGN.PHILOSOPHY
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            The System Aesthetic
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed mb-8">
            DayCheckin rejects consumer SaaS design. No rounded corners. No pastel gradients.
            Every element is a sharp-edged box signifying stability and technical rigor.
          </p>
          <p className="text-text-secondary text-lg leading-relaxed">
            You don't "use" this app—you <span className="text-cyan">Initialize</span> the workspace
            and <span className="text-cyan">Terminate</span> the session. Every action timestamped.
            Every completion logged.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono text-text-dim text-sm mb-6 uppercase tracking-widest">
            READY.TO.INITIALIZE
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Execution Cycle
          </h2>
          <p className="text-text-secondary text-lg mb-12 max-w-2xl mx-auto">
            Join technical teams who value precision, transparency, and deep work.
          </p>
          <a
            href={config.signupUrl}
            className="inline-block px-10 py-5 bg-[#FFD93D] text-black font-mono uppercase tracking-wider text-lg hover:opacity-90 transition-opacity cursor-pointer"
          >
            Initialize System Workspace
          </a>
        </div>
      </section>
    </div>
  );
}
